/**
 * CategorySelector.jsx
 * Auswahl der Leistungskategorie für Zeit- und Projekterfassung
 * Zeigt Hauptkategorien mit optionaler Unterkategorie-Auswahl
 */
import { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '../context/AuthContext';
import { Label } from './ui/label';
import { Switch } from './ui/switch';
import { Badge } from './ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';
import { Tag, AlertTriangle } from 'lucide-react';

export function CategorySelector({
  value,
  onChange,
  projectId,
  billableOverride,
  onBillableOverrideChange,
  onValidityChange,
  disabled = false,
  showBillableSwitch = true,
  existingEntryCategory = null, // For editing existing entries with deactivated categories
  activityId = '', // L1: Tätigkeit (ASA-Sitzung, Begehung …) aus der Liste der Hauptkategorie
  onActivityChange = null,
}) {
  const { api } = useAuth();
  const [categories, setCategories] = useState([]);
  const [flatCategories, setFlatCategories] = useState([]);
  const [projectCategory, setProjectCategory] = useState(null);
  const [selectedMainCategory, setSelectedMainCategory] = useState('');
  const [selectedSubCategory, setSelectedSubCategory] = useState('');
  const [resolvedBillable, setResolvedBillable] = useState(true);
  const [loading, setLoading] = useState(true);
  
  // Track previous projectId to detect changes
  const prevProjectIdRef = useRef(projectId);

  // Reset category selection when project changes (but not on first render)
  useEffect(() => {
    const prevProjectId = prevProjectIdRef.current;
    prevProjectIdRef.current = projectId;
    
    // Only reset if:
    // 1. There was a previous project (not first render)
    // 2. The project actually changed
    if (prevProjectId && prevProjectId !== projectId) {
      setSelectedMainCategory('');
      setSelectedSubCategory('');
      onChange('');
      if (onActivityChange) onActivityChange('');
    }
  }, [projectId, onChange, onActivityChange]);

  // Fetch categories
  const fetchCategories = useCallback(async () => {
    try {
      const [treeRes, flatRes] = await Promise.all([
        api.get('/service-categories?include_inactive=true'),
        api.get('/service-categories/flat?include_inactive=true'),
      ]);
      setCategories(treeRes.data);
      setFlatCategories(flatRes.data);
    } catch (error) {
      console.error('Failed to fetch categories:', error);
    } finally {
      setLoading(false);
    }
  }, [api]);

  // Fetch project's default category
  const fetchProjectCategory = useCallback(async () => {
    if (!projectId) {
      setProjectCategory(null);
      return;
    }
    try {
      const res = await api.get(`/projects/${projectId}`);
      if (res.data.category_id) {
        setProjectCategory(res.data.category_id);
      } else {
        setProjectCategory(null);
      }
    } catch (error) {
      console.error('Failed to fetch project category:', error);
      setProjectCategory(null);
    }
  }, [api, projectId]);

  useEffect(() => {
    fetchCategories();
  }, [fetchCategories]);

  useEffect(() => {
    fetchProjectCategory();
  }, [fetchProjectCategory]);

  // Initialize selection based on value or project default
  useEffect(() => {
    if (value) {
      // Find the category and its parent
      const flat = flatCategories.find(c => c.id === value);
      if (flat) {
        if (flat.parent_id) {
          setSelectedMainCategory(flat.parent_id);
          setSelectedSubCategory(value);
        } else {
          setSelectedMainCategory(value);
          setSelectedSubCategory('');
        }
      }
    } else if (projectCategory && !value) {
      // Use project default if no value set
      const flat = flatCategories.find(c => c.id === projectCategory);
      if (flat) {
        if (flat.parent_id) {
          setSelectedMainCategory(flat.parent_id);
          setSelectedSubCategory(projectCategory);
        } else {
          setSelectedMainCategory(projectCategory);
          setSelectedSubCategory('');
        }
        // Trigger onChange with project default
        onChange(projectCategory);
      }
    }
  }, [value, projectCategory, flatCategories, onChange]);

  // Update resolved billable when category changes
  useEffect(() => {
    const categoryId = selectedSubCategory || selectedMainCategory;
    if (categoryId) {
      const category = flatCategories.find(c => c.id === categoryId);
      if (category) {
        setResolvedBillable(category.billable_default);
      }
    } else {
      setResolvedBillable(true); // Default
    }
  }, [selectedMainCategory, selectedSubCategory, flatCategories]);

  // Get main categories (only active, unless existing entry has deactivated one)
  const mainCategories = categories.filter(c => {
    if (c.is_active) return true;
    // Show deactivated category if it's the existing entry's category
    if (existingEntryCategory) {
      const existingFlat = flatCategories.find(f => f.id === existingEntryCategory);
      if (existingFlat) {
        if (!existingFlat.parent_id && existingFlat.id === c.id) return true;
        if (existingFlat.parent_id === c.id) return true;
      }
    }
    return false;
  });

  // Get subcategories for selected main category
  const getSubCategories = () => {
    if (!selectedMainCategory) return [];
    const mainCatTree = categories.find(c => c.id === selectedMainCategory);
    if (!mainCatTree?.children?.length) return [];
    
    return mainCatTree.children.filter(c => {
      if (c.is_active) return true;
      // Show deactivated subcategory if it's the existing entry's category
      if (existingEntryCategory === c.id) return true;
      return false;
    });
  };

  const subCategories = getSubCategories();

  // Determine if subcategory selection is enabled/required based on main category flag
  const mainCat = flatCategories.find(c => c.id === selectedMainCategory);
  const subSelectable = !!mainCat?.employee_selectable_sub;

  // L1: Tätigkeiten der Hauptkategorie (aktive; eine deaktivierte nur, wenn die Buchung sie schon trägt)
  const activities = (mainCat?.activities || [])
    .filter((a) => a && (a.active !== false || a.id === activityId))
    .sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0));
  const activityRequired = !!mainCat?.activity_required && activities.some((a) => a.active !== false);
  const showActivityUI = !!projectId && !!onActivityChange && activities.length > 0;

  // Validity check: Unterkategorie (falls wählbar) und Tätigkeit (falls Pflicht)
  useEffect(() => {
    if (!onValidityChange) return;
    
    // If no project selected, always valid (don't block button)
    if (!projectId) {
      onValidityChange(true);
      return;
    }
    
    let isValid = true;
    // If subSelectable is true and there are subcategories available: must have a subcategory selected
    if (subSelectable && subCategories.length > 0 && !selectedSubCategory) {
      isValid = false;
    }
    if (showActivityUI && activityRequired && !activityId) {
      isValid = false;
    }
    onValidityChange(isValid);
  }, [onValidityChange, projectId, subSelectable, subCategories.length, selectedSubCategory, showActivityUI, activityRequired, activityId]);

  // Handle main category change
  const handleMainCategoryChange = (catId) => {
    setSelectedMainCategory(catId);
    setSelectedSubCategory('');
    
    // If the main category has children, don't set value yet (user must pick subcategory or confirm main)
    const mainCat = categories.find(c => c.id === catId);
    if (!mainCat?.children?.length) {
      // No children - set this as the value
      onChange(catId);
    } else {
      // Has children - clear value until subcategory is selected, or user can use main
      onChange(catId);
    }
  };

  // Handle subcategory change
  const handleSubCategoryChange = (subId) => {
    setSelectedSubCategory(subId);
    onChange(subId || selectedMainCategory);
  };

  // Get the currently selected category for display
  const getCurrentCategory = () => {
    const categoryId = selectedSubCategory || selectedMainCategory;
    if (!categoryId) return null;
    return flatCategories.find(c => c.id === categoryId);
  };

  const currentCategory = getCurrentCategory();
  const effectiveBillable = billableOverride !== null && billableOverride !== undefined 
    ? billableOverride 
    : resolvedBillable;

  if (loading) {
    return <div className="h-10 bg-muted/50 rounded animate-pulse" />;
  }

  // Determine visibility conditions
  const showCategoryUI = projectId && subSelectable && subCategories.length > 0;
  const hideEverything = !projectId || (!showCategoryUI && !showActivityUI);

  // If nothing to show, return null (hooks already ran above, category still gets set correctly)
  if (hideEverything) {
    return null;
  }

  // Get main category info for display
  const mainCatDisplay = mainCat || flatCategories.find(c => c.id === selectedMainCategory);

  return (
    <div className="space-y-3">
      {/* Only show when subSelectable is true (employee can/must choose subcategory) */}
      {showCategoryUI && (
        <>
          {/* Main Category - Info only (not editable) */}
          <div className="space-y-2">
            <Label className="text-muted-foreground flex items-center gap-2">
              <Tag className="w-3.5 h-3.5" />
              Leistungskategorie
            </Label>
            {mainCatDisplay && (
              <div className="flex items-center gap-2 px-3 py-2 bg-muted/30 rounded-md border border-border/50">
                {mainCatDisplay.color && (
                  <div 
                    className="w-2.5 h-2.5 rounded-full flex-shrink-0" 
                    style={{ backgroundColor: mainCatDisplay.color }} 
                  />
                )}
                <span className="text-sm text-foreground">{mainCatDisplay.name}</span>
                <span className="text-xs text-muted-foreground ml-auto">Projekt-Standard</span>
              </div>
            )}
          </div>

          {/* Subcategory - Required selection */}
          <div className="space-y-2">
            <Label className="text-sm text-foreground font-medium">
              Unterkategorie (Pflicht)
            </Label>
            <Select
              value={selectedSubCategory || undefined}
              onValueChange={(val) => handleSubCategoryChange(val)}
              disabled={disabled}
            >
              <SelectTrigger 
                data-testid="sub-category-select" 
                className={`bg-input border-border text-foreground ${
                  !selectedSubCategory ? 'border-amber-500/50 ring-1 ring-amber-500/30' : ''
                }`}
              >
                <SelectValue placeholder="Unterkategorie wählen" />
              </SelectTrigger>
              <SelectContent className="bg-card border-border">
                {/* No "Nur Hauptkategorie" option in required case */}
                {subCategories.map((cat) => (
                  <SelectItem key={cat.id} value={cat.id}>
                    <span className={!cat.is_active ? 'text-muted-foreground line-through' : ''}>
                      {cat.name}
                    </span>
                    {!cat.is_active && (
                      <Badge variant="secondary" className="text-[10px] bg-muted text-muted-foreground ml-1">
                        Deaktiviert
                      </Badge>
                    )}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            
            {/* Validation warning when subcategory is required but not selected */}
            {!selectedSubCategory && (
              <div className="flex items-center gap-1.5 p-2 rounded bg-amber-500/10 border border-amber-500/30">
                <AlertTriangle className="w-3.5 h-3.5 text-amber-600 dark:text-amber-400 flex-shrink-0" />
                <p className="text-xs text-amber-600 dark:text-amber-400">
                  Bitte Unterkategorie wählen (Pflicht für diese Kategorie).
                </p>
              </div>
            )}
          </div>

          {/* Billable Switch - shown in subSelectable case */}
          {showBillableSwitch && currentCategory && (
            <div className="flex items-center justify-between p-3 rounded-lg bg-muted/50 border border-border/50">
              <div className="flex-1">
                <div className="flex items-center gap-2">
                  <Label className="text-muted-foreground text-sm">Abrechenbar</Label>
                  {billableOverride !== null && billableOverride !== undefined && (
                    <Badge variant="outline" className="text-[10px] border-amber-500/50 text-amber-600 dark:text-amber-400">
                      Überschrieben
                    </Badge>
                  )}
                </div>
                <p className="text-xs text-muted-foreground mt-0.5">
                  Standard: {currentCategory.billable_default ? 'Ja' : 'Nein'}
                </p>
              </div>
              <Switch
                checked={effectiveBillable}
                onCheckedChange={(checked) => {
                  // Only set override if different from category default
                  if (checked !== resolvedBillable) {
                    onBillableOverrideChange(checked);
                  } else {
                    onBillableOverrideChange(null); // Clear override
                  }
                }}
                disabled={disabled}
                data-testid="billable-override-switch"
                className={!effectiveBillable ? 'data-[state=unchecked]:bg-muted-foreground' : ''}
              />
            </div>
          )}

          {/* Deactivated category warning */}
          {currentCategory && !currentCategory.is_active && (
            <div className="flex items-start gap-2 p-2 rounded bg-amber-500/10 border border-amber-500/30">
              <AlertTriangle className="w-4 h-4 text-amber-600 dark:text-amber-400 mt-0.5 flex-shrink-0" />
              <p className="text-xs text-amber-600 dark:text-amber-400">
                Diese Kategorie wurde deaktiviert. Bei Änderung muss eine aktive Kategorie gewählt werden.
              </p>
            </div>
          )}
        </>
      )}

      {/* L1: Tätigkeit */}
      {showActivityUI && (
        <div className="space-y-2">
          <Label className="text-sm text-foreground font-medium">
            Tätigkeit{activityRequired ? ' (Pflicht)' : ''}
          </Label>
          <Select
            value={activityId || undefined}
            onValueChange={(val) => onActivityChange(val === '__none__' ? '' : val)}
            disabled={disabled}
          >
            <SelectTrigger
              data-testid="activity-select"
              className={`bg-input border-border text-foreground ${
                activityRequired && !activityId ? 'border-amber-500/50 ring-1 ring-amber-500/30' : ''
              }`}
            >
              <SelectValue placeholder="Tätigkeit wählen" />
            </SelectTrigger>
            <SelectContent className="bg-card border-border">
              {!activityRequired && <SelectItem value="__none__">Keine Angabe</SelectItem>}
              {activities.map((a) => (
                <SelectItem key={a.id} value={a.id}>
                  <span className={a.active === false ? 'text-muted-foreground line-through' : ''}>{a.name}</span>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {activityRequired && !activityId && (
            <div className="flex items-center gap-1.5 p-2 rounded bg-amber-500/10 border border-amber-500/30">
              <AlertTriangle className="w-3.5 h-3.5 text-amber-600 dark:text-amber-400 flex-shrink-0" />
              <p className="text-xs text-amber-600 dark:text-amber-400">
                Bitte Tätigkeit wählen – sie erscheint so auf der Rechnung.
              </p>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

/**
 * CategoryTag - Kompakte Anzeige einer Kategorie (für Listen)
 */
export function CategoryTag({ categoryId, categories, small = false }) {
  if (!categoryId || !categories?.length) return null;
  
  const category = categories.find(c => c.id === categoryId);
  if (!category) return null;

  const sizeClasses = small 
    ? 'px-1.5 py-0.5 text-[10px]' 
    : 'px-2 py-0.5 text-xs';

  return (
    <span
      className={`inline-flex items-center gap-1 rounded ${sizeClasses} border`}
      style={{
        backgroundColor: category.color ? `${category.color}20` : 'rgba(113, 113, 122, 0.2)',
        borderColor: category.color ? `${category.color}50` : 'rgba(113, 113, 122, 0.3)',
        color: category.color || '#a1a1aa'
      }}
      title={category.display_name || category.name}
    >
      {category.color && (
        <span 
          className="w-1.5 h-1.5 rounded-full" 
          style={{ backgroundColor: category.color }} 
        />
      )}
      <span className="truncate max-w-[80px]">{category.name}</span>
    </span>
  );
}

/**
 * BillableIndicator - Zeigt an ob ein Eintrag abrechenbar ist
 */
export function BillableIndicator({ billable, small = false }) {
  if (billable) return null; // Don't show indicator for billable entries (default)
  
  const sizeClasses = small ? 'text-[10px] px-1' : 'text-xs px-1.5';
  
  return (
    <span 
      className={`${sizeClasses} py-0.5 rounded bg-muted/50 text-muted-foreground border border-border/50`}
      title="Nicht abrechenbar"
    >
      N/A
    </span>
  );
}

export default CategorySelector;
